import { Linkedin, UserRound, Wrench } from "lucide-react";
import { useTranslation } from "react-i18next";

import type { PlaySuggestion } from "./playSuggestions";
import { groupPlaySuggestions } from "./playSuggestions";

type Props = {
  suggestions: PlaySuggestion[];
  usingId?: string | null;
  onUse: (suggestion: PlaySuggestion) => void;
};

function SuggestionCard({
  suggestion,
  using,
  onUse,
}: {
  suggestion: PlaySuggestion;
  using: boolean;
  onUse: () => void;
}) {
  const { t } = useTranslation();
  const isSignal = suggestion.category === "buying_signals";

  return (
    <article className="flex min-h-[9.5rem] flex-col rounded-xl border border-slate-200 bg-white p-4 shadow-[0_1px_0_rgba(15,23,42,0.02)] transition-colors hover:border-slate-300">
      <p className="text-[11px] font-semibold uppercase tracking-wide text-violet-600">
        {t(suggestion.tag)}
      </p>
      <p className="mt-2 flex-1 text-sm font-medium leading-snug text-slate-900">
        {suggestion.audience}
      </p>
      <div className="mt-4 flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5 text-slate-400">
          {isSignal ? (
            <>
              <span className="flex h-6 w-6 items-center justify-center rounded-md bg-slate-50">
                {suggestion.kind === "hiring_role" ? (
                  <UserRound className="h-3.5 w-3.5" />
                ) : (
                  <Wrench className="h-3.5 w-3.5" />
                )}
              </span>
              <span className="flex h-6 w-6 items-center justify-center rounded-md bg-slate-50">
                <Linkedin className="h-3.5 w-3.5" />
              </span>
            </>
          ) : (
            <span className="flex h-6 w-6 items-center justify-center rounded-md bg-slate-50">
              <Linkedin className="h-3.5 w-3.5" />
            </span>
          )}
        </div>
        <button
          type="button"
          disabled={using}
          onClick={onUse}
          className="text-sm font-medium text-violet-600 hover:text-violet-700 disabled:cursor-wait disabled:opacity-60"
        >
          {using ? t("Creating…") : t("Use this play →")}
        </button>
      </div>
    </article>
  );
}

function SuggestionGroup({
  title,
  items,
  usingId,
  onUse,
}: {
  title: string;
  items: PlaySuggestion[];
  usingId?: string | null;
  onUse: (suggestion: PlaySuggestion) => void;
}) {
  const { t } = useTranslation();
  if (items.length === 0) return null;
  return (
    <section className="space-y-3">
      <h3 className="text-sm font-semibold text-slate-800">{t(title)}</h3>
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
        {items.map((s) => (
          <SuggestionCard
            key={s.id}
            suggestion={s}
            using={usingId === s.id}
            onUse={() => onUse(s)}
          />
        ))}
      </div>
    </section>
  );
}

export default function PlaySuggestions({ suggestions, usingId, onUse }: Props) {
  const { t } = useTranslation();
  const { buyerProfiles, buyingSignals } = groupPlaySuggestions(suggestions);

  if (buyerProfiles.length === 0 && buyingSignals.length === 0) return null;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-base font-semibold text-slate-900">{t("Play suggestions")}</h2>
        <p className="mt-0.5 text-sm text-slate-500">
          {t("Ideas from your brain — personas, ICP, and company signals.")}
        </p>
      </div>
      <SuggestionGroup
        title="Buyer profiles (role, company, location)"
        items={buyerProfiles}
        usingId={usingId}
        onUse={onUse}
      />
      <SuggestionGroup
        title="Buying signals (hiring, tools, LinkedIn posts)"
        items={buyingSignals}
        usingId={usingId}
        onUse={onUse}
      />
    </div>
  );
}
